import { useEffect } from "react";
import { Step } from "../../types/TransactionTemplate.types";
import { useKeyboardNavigation } from "./useKeyboardNavigation";

interface UseTransactionKeyboardNavigationParams {
  steps: Step[];
  isEditMode: boolean;
  selectedPrimitive: { stepIndex: number; primIndex: number } | null;
  editingPrimitiveText: {
    stepIndex: number;
    primIndex: number;
    text: string;
  } | null;
  setIsEditMode: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedPrimitive: React.Dispatch<
    React.SetStateAction<{ stepIndex: number; primIndex: number } | null>
  >;
  setEditingPrimitiveText: React.Dispatch<
    React.SetStateAction<{
      stepIndex: number;
      primIndex: number;
      text: string;
    } | null>
  >;
  setAddMode: React.Dispatch<
    React.SetStateAction<{
      afterPrimitive: { stepIndex: number; primIndex: number };
      branch?: "yes" | "no" | "after";
    } | null>
  >;
  setIsShapeLibraryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onCloseEditPanel: () => void;
  onDeleteSelected: () => void;
  onSave: () => void;
}

export function useTransactionKeyboardNavigation({
  steps,
  isEditMode,
  selectedPrimitive,
  editingPrimitiveText,
  setIsEditMode,
  setSelectedPrimitive,
  setEditingPrimitiveText,
  setAddMode,
  setIsShapeLibraryOpen,
  onDeleteSelected,
  onCloseEditPanel,
  onSave,
}: UseTransactionKeyboardNavigationParams) {
  const stepIndex = selectedPrimitive?.stepIndex ?? 0;
  const step = steps[stepIndex];
  const primitives = step?.primitives ?? [];

  const eventAsPrimitive = step?.event
    ? [
        {
          id: step.event.id,
          name: step.event.name,
          x: step.event.x,
          y: step.event.y,
          type: "primitive" as const,
          data: step.event.data,
        },
      ]
    : [];

  const allPrimitives = [...eventAsPrimitive, ...primitives];

  const selectedIndex =
    selectedPrimitive === null
      ? null
      : selectedPrimitive.primIndex === -1
        ? 0
        : selectedPrimitive.primIndex + 1;

  const openShapeLibrary = (
    primitive: { stepIndex: number; primIndex: number },
    branch: "yes" | "no" | "after",
  ) => {
    setAddMode({ afterPrimitive: primitive, branch });
    setIsShapeLibraryOpen(true);
  };

  useKeyboardNavigation({
    primitives: allPrimitives,
    selectedIndex,
    isEditMode,
    setIsEditMode,
    disableEmptySelect: true,
    onSelect: (index) => {
      if (index === null) {
        setSelectedPrimitive(null);
      } else if (index === 0 && step?.event) {
        setSelectedPrimitive({ stepIndex, primIndex: -1 });
      } else {
        setSelectedPrimitive({ stepIndex, primIndex: index - 1 });
      }
    },
    onOpenLibrary: (branch) => {
      if (selectedPrimitive) {
        openShapeLibrary(selectedPrimitive, branch);
      }
    },
    onDelete: onDeleteSelected,
    onCloseEditPanel,
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      const isArrow = [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ].includes(e.key);

      if (e.ctrlKey && e.code === "KeyS") {
        e.preventDefault();
        onSave();
        return;
      }

      if (editingPrimitiveText && e.key === "Escape") {
        e.preventDefault();
        setEditingPrimitiveText(null);
        return;
      }

      if (isInput && !e.altKey && !(e.ctrlKey && isArrow)) return;

      if (!isEditMode) return;

      if (
        !selectedPrimitive &&
        !editingPrimitiveText &&
        e.altKey &&
        !e.ctrlKey &&
        isArrow
      ) {
        e.preventDefault();
        const firstStep = steps[0];
        if (firstStep?.event) {
          setSelectedPrimitive({ stepIndex: 0, primIndex: -1 });
        } else if (firstStep?.primitives.length > 0) {
          setSelectedPrimitive({ stepIndex: 0, primIndex: 0 });
        }
        return;
      }

      if (!selectedPrimitive && !editingPrimitiveText && e.ctrlKey && isArrow) {
        if (e.code === "ArrowRight" || e.code === "ArrowDown") {
          e.preventDefault();
          const firstStep = steps[0];
          if (firstStep) {
            const lastPrim =
              firstStep.primitives.length > 0
                ? { stepIndex: 0, primIndex: firstStep.primitives.length - 1 }
                : { stepIndex: 0, primIndex: -1 };
            setAddMode({
              afterPrimitive: lastPrim,
              branch: "after",
            });
            setIsShapeLibraryOpen(true);
          }
        }
        return;
      }

      if (
        selectedPrimitive &&
        selectedPrimitive.primIndex >= 0 &&
        !editingPrimitiveText &&
        /^Digit[1-7]$/.test(e.code)
      ) {
        const prim = step?.primitives[selectedPrimitive.primIndex];
        if (!prim) return;
        e.preventDefault();
        setEditingPrimitiveText({
          stepIndex,
          primIndex: selectedPrimitive.primIndex,
          text: prim.name,
        });
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedPrimitive,
    editingPrimitiveText,
    steps,
    isEditMode,
    step,
    stepIndex,
    onSave,
  ]);
}
